import { useState, useCallback, useRef, useEffect } from "react";
import { z } from "zod";
import { fetchFastApi } from "../api/fastapi";
import { toast } from "sonner";
import { useAudit } from "../contexts/auditContext";
import { pollJob } from "../lib/pollJob";

// `status` stays a plain string: anything but the three terminal states
// below (cancel_requested, unknown, ...) means keep polling.
const WorkPaperJobStatusSchema = z.object({
  status: z.string(),
  download_url: z.string().nullish(),
  filename: z.string().nullish(),
  error: z.string().nullish(),
});

const TERMINAL_STATUSES = new Set(["success", "failure", "cancelled"]);

interface UseAsyncWorkPaperDownloadOptions {
  intervalMs?: number;
  timeoutMs?: number;
}

export const useAsyncWorkPaperDownload = (
  options: UseAsyncWorkPaperDownloadOptions = {},
) => {
  const { intervalMs = 3000, timeoutMs = 30 * 60 * 1000 } = options;
  // Both export paths are entity-specific, so the hook needs to know which
  // parent it is exporting for. Read from the audit context rather than adding
  // a parameter: the only consumer (AuditWorkPaperV2Page) already sits inside
  // AuditProvider, and useAudit throws rather than defaulting if that changes.
  const { sessionKind } = useAudit();
  const [downloading, setDownloading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const downloadFile = useCallback((downloadUrl: string, filename: string) => {
    try {
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  }, []);

  const pollJobStatus = useCallback(
    async (taskId: string): Promise<void> => {
      const toastId = `work-paper-download:${taskId}`;
      toast.loading("Generating workpaper…", {
        id: toastId,
        description: "This may take a few minutes for large workpapers.",
      });

      const result = await pollJob({
        path: `/sessions/work-paper-job/${encodeURIComponent(taskId)}`,
        schema: WorkPaperJobStatusSchema,
        isTerminal: (jobStatus) => TERMINAL_STATUSES.has(jobStatus.status),
        intervalMs,
        deadlineMs: timeoutMs,
        // Unbounded: a failed poll is retried until the deadline.
        maxConsecutiveFailures: Number.POSITIVE_INFINITY,
        isCancelled: () => !isMountedRef.current,
      });

      if (result.kind === "cancelled") {
        return;
      }
      if (isMountedRef.current) {
        setDownloading(false);
      }

      switch (result.kind) {
        case "done":
          break;
        // Failures are unbounded, so a poll only ends at its deadline.
        case "failed":
        case "timed_out":
          toast.error("Workpaper generation timed out", {
            id: toastId,
            description: "Please try again or contact support.",
          });
          return;
        default: {
          const unreachable: never = result;
          return unreachable;
        }
      }

      const jobStatus = result.status;
      switch (jobStatus.status) {
        case "success":
          if (jobStatus.download_url && jobStatus.filename) {
            downloadFile(jobStatus.download_url, jobStatus.filename);
            toast.success("Workpaper ready", {
              id: toastId,
              description: "Your workpaper has been downloaded.",
            });
          } else {
            toast.error("Download URL not available", { id: toastId });
          }
          return;
        case "failure":
          toast.error("Failed to generate workpaper", {
            id: toastId,
            description: jobStatus.error || "An error occurred",
          });
          return;
        case "cancelled":
          toast.error("Workpaper generation cancelled", { id: toastId });
          return;
        default:
          // isTerminal admits only the statuses above.
          toast.error("Failed to generate workpaper", { id: toastId });
      }
    },
    [timeoutMs, intervalMs, downloadFile],
  );

  // Both the plain workpaper and the evidence bundle share the async-job machinery: POST a start
  // endpoint, then poll the same /work-paper-job/{task_id} for the download URL. They differ only in
  // the start path.
  const startAsyncJob = useCallback(
    async (path: string) => {
      setDownloading(true);

      try {
        const response = await fetchFastApi({ path, method: "POST" });

        const taskId = response?.task_id;
        if (!taskId) {
          throw new Error("No task ID returned from server");
        }

        await pollJobStatus(taskId);
      } catch (error) {
        console.error("Error starting workpaper generation:", error);
        toast.error("Failed to start workpaper generation");
        if (isMountedRef.current) {
          setDownloading(false);
        }
      }
    },
    [pollJobStatus],
  );

  const startWorkPaperDownload = useCallback(
    async (sessionId: string, templateId?: string | null) => {
      if (!sessionId) {
        toast.error("No session ID provided");
        return;
      }
      const queryParam = templateId ? `?template_id=${templateId}` : "";
      // `/sessions/{id}/generate-work-paper-async` resolves its id against
      // ControlSessionRecord, so it is control-only despite the neutral-looking
      // path. `sessionKind` picks the work-step sibling instead.
      await startAsyncJob(
        sessionKind === "work_step"
          ? `/work-steps/work-step-session/${sessionId}/generate-work-paper-async${queryParam}`
          : `/sessions/${sessionId}/generate-work-paper-async${queryParam}`,
      );
    },
    [startAsyncJob, sessionKind],
  );

  // Evidence bundle: combined template workpaper + annotated source documents, zipped. Gated behind
  // the auditor-trail feature; the BE endpoint 403s when the flag is off.
  const startBundleDownload = useCallback(
    async (sessionId: string, templateId?: string | null) => {
      if (!sessionId) {
        toast.error("No session ID provided");
        return;
      }
      const queryParam = templateId ? `?template_id=${templateId}` : "";
      // Same split as the plain export: the neutral-looking `/sessions/{id}`
      // path resolves its id against ControlSessionRecord. The work-step
      // sibling queues the same bundle workflow and seeds the same job status,
      // so both kinds poll the shared `/sessions/work-paper-job/{taskId}` above.
      await startAsyncJob(
        sessionKind === "work_step"
          ? `/work-steps/work-step-session/${sessionId}/generate-work-paper-bundle-async${queryParam}`
          : `/sessions/${sessionId}/generate-work-paper-bundle-async${queryParam}`,
      );
    },
    [startAsyncJob, sessionKind],
  );

  return {
    startWorkPaperDownload,
    startBundleDownload,
    downloading,
  };
};
